import { useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { queryClient } from "App";
import {
  addCustomFieldIssues,
  BaseForm,
  buildCustomFieldDefaults,
  buildCustomFieldSelection,
  customFieldsPayload,
  customFieldsSchema,
  CustomFieldsSection,
  FormCombobox,
  FormDateTimePicker,
  FormInput,
  FormMultiSelect,
  FormSelect,
  FormSkeleton,
  FormTextarea,
  FormToggle,
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import { getUserTimeZone } from "components/helpers";
import { useCampaignFormOptions } from "routes/campaign/forms/useCampaignFormOptions";
import CampaignService from "services/Campaign";

const buildSchema = configs =>
  z
    .object({
      name: z.string().trim().min(1, "A name is required"),
      status: z.number({
        required_error: "A status is required",
        invalid_type_error: "A status is required",
      }),
      assigned_user_id: z.number({
        required_error: "An assignee is required",
        invalid_type_error: "An assignee is required",
      }),
      start_time: z
        .string({ invalid_type_error: "A start time is required" })
        .min(1, "A start time is required"),
      end_time: z.string().nullable(),
      form_id: z.number().nullable(),
      limit_email_templates: z.boolean(),
      email_templates: z.array(z.number()),
      email_service: z.number({
        required_error: "An email service is required",
        invalid_type_error: "An email service is required",
      }),
      email_type: z.string().min(1, "An email type is required"),
      region_id: z.number().nullable(),
      case_campaign_status_preset: z.number().nullable(),
      campaign_status_preset: z.number().nullable(),
      revenue_status_preset: z.number().nullable(),
      revenue_recipient_preset: z.number().nullable(),
      description: z.string().trim().min(1, "A description is required"),
      custom_fields: customFieldsSchema,
    })
    .superRefine((values, ctx) => {
      if (
        values.start_time &&
        values.end_time &&
        values.end_time <= values.start_time
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["end_time"],
          message: "End time must be after the start time",
        });
      }
      addCustomFieldIssues(configs, values.custom_fields, ctx);
    });

const buildDefaults = (campaign, configs) => ({
  name: campaign.name ?? "",
  status: campaign.status ?? null,
  assigned_user_id: campaign.assigned_user_id ?? null,
  start_time: campaign.start_time ?? null,
  end_time: campaign.end_time ?? null,
  form_id: campaign.form_id ?? null,
  limit_email_templates: (campaign.info?.selected_templates ?? []).length > 0,
  email_templates: campaign.info?.selected_templates ?? [],
  email_service: campaign.email_service ?? null,
  email_type: campaign.email_type ?? "",
  region_id: campaign.region_id ?? null,
  case_campaign_status_preset:
    campaign.info?.case_campaign_status_preset ?? null,
  campaign_status_preset: campaign.info?.campaign_status_preset ?? null,
  revenue_status_preset: campaign.info?.revenue_status_preset ?? null,
  revenue_recipient_preset: campaign.info?.revenue_recipient_preset ?? null,
  description: campaign.info?.description ?? "",
  custom_fields: buildCustomFieldDefaults(configs, campaign.custom_fields),
});

const CampaignFormFields = ({ options, selection, setSelection, groups }) => {
  const limitTemplates = useWatch({ name: "limit_email_templates" });
  const timeZone = getUserTimeZone();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
            <FormInput name="name" label="Name" required />
            <FormSelect
              name="status"
              label="Status"
              required
              options={options.statusOptions}
            />
            <FormCombobox
              name="assigned_user_id"
              label="Assignee"
              required
              options={options.userOptions}
            />
            <FormCombobox
              name="form_id"
              label="Form"
              clearable
              options={options.formOptions}
            />
            <FormDateTimePicker
              name="start_time"
              label="Start Time"
              required
              timeZone={timeZone}
            />
            <FormDateTimePicker
              name="end_time"
              label="End Time"
              timeZone={timeZone}
            />
            <FormTextarea
              name="description"
              label="Description"
              required
              rows={3}
              className="tw:sm:col-span-2"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
            <FormSelect
              name="email_service"
              label="Email Service"
              required
              options={options.emailServiceOptions}
            />
            <FormSelect
              name="email_type"
              label="Email Type"
              required
              options={options.emailTypeOptions}
            />
            <FormToggle
              name="limit_email_templates"
              label="Limit Email Templates"
              className="tw:sm:col-span-2"
            />
            {limitTemplates && (
              <FormMultiSelect
                name="email_templates"
                label="Templates"
                options={options.templateOptions}
                className="tw:sm:col-span-2"
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Presets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
            <FormCombobox
              name="region_id"
              label="Region"
              clearable
              options={options.regionOptions}
            />
            <FormCombobox
              name="case_campaign_status_preset"
              label="Case Campaign Status Preset"
              clearable
              options={options.campaignStatusPresetOptions}
            />
            <FormCombobox
              name="campaign_status_preset"
              label="Campaign Status Preset"
              clearable
              options={options.campaignStatusPresetOptions}
            />
            <FormCombobox
              name="revenue_status_preset"
              label="Revenue Opportunity Status Preset"
              clearable
              options={options.revenueStatusPresetOptions}
            />
            <FormCombobox
              name="revenue_recipient_preset"
              label="Revenue Opportunity Recipient Preset"
              clearable
              options={options.revenueRecipientPresetOptions}
            />
          </div>
        </CardContent>
      </Card>

      <CustomFieldsSection
        selection={selection}
        setSelection={setSelection}
        groups={groups}
      />
    </>
  );
};

const saveCampaign = (values, configs, campaign) => {
  const shared = {
    name: values.name,
    status: values.status,
    // The DTO types region_id and form_id as non-nullable integers, so a
    // cleared value has to drop the key rather than send null.
    region: values.region_id || undefined,
    form: values.form_id || undefined,
    emailService: values.email_service,
    emailType: values.email_type,
    description: values.description,
    emailTemplates: values.limit_email_templates ? values.email_templates : [],
    custom: customFieldsPayload(configs, values.custom_fields),
    campaignStatusPreset: values.campaign_status_preset || null,
    caseCampaignStatusPreset: values.case_campaign_status_preset || null,
    revenueStatusPreset: values.revenue_status_preset || null,
    revenueRecipientPreset: values.revenue_recipient_preset || null,
  };
  return campaign.id != null
    ? CampaignService.editCampaign({
        ...shared,
        campaignId: campaign.id,
        assigned: values.assigned_user_id,
        start: values.start_time,
        end: values.end_time || null,
      })
    : CampaignService.createCampaign({
        ...shared,
        assigned_user_id: values.assigned_user_id,
        start_time: values.start_time,
        end_time: values.end_time || null,
      });
};

const EMPTY_CAMPAIGN = {};

const CampaignFormInner = ({
  campaign,
  configs,
  groups,
  onCancel,
  onSuccess,
}) => {
  const isEdit = campaign.id != null;
  const [selection, setSelection] = useState(() =>
    buildCustomFieldSelection(
      configs,
      isEdit ? (campaign.custom_fields ?? {}) : null
    )
  );
  const options = useCampaignFormOptions(campaign.assigned_user_id ?? null);
  const schema = useMemo(() => buildSchema(configs), [configs]);
  const defaultValues = useMemo(
    () => buildDefaults(campaign, configs),
    [campaign, configs]
  );

  const save = useMutation({
    mutationFn: values => saveCampaign(values, configs, campaign),
    onSuccess: async result => {
      await queryClient.invalidateQueries({ queryKey: ["campaign"] });
      toast.success(isEdit ? "Campaign updated" : "Campaign created");
      onSuccess?.(result);
    },
  });

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={values => save.mutateAsync(values)}
      onCancel={onCancel}
      submitButtonText={isEdit ? "Save Campaign" : "Create Campaign"}
    >
      <CampaignFormFields
        options={options}
        selection={selection}
        setSelection={setSelection}
        groups={groups}
      />
    </BaseForm>
  );
};

/**
 * Create or edit form for a campaign. Without `campaignId` it creates one and
 * passes the created record to `onSuccess`; with it, it edits that campaign.
 *
 * The campaign, the custom-field configs, and the groups gate the skeleton
 * because they provide `defaultValues` and the schema, which BaseForm reads once
 * on mount. The option lists may load later.
 */
const CampaignForm = ({ campaignId, onCancel, onSuccess }) => {
  const isEdit = campaignId != null;
  const {
    data: campaign,
    isSuccess: campaignLoaded,
    isError: campaignError,
  } = useQuery({
    queryKey: ["campaign", String(campaignId)],
    queryFn: () => CampaignService.getCampaign(campaignId),
    enabled: isEdit,
    meta: { errorMessage: "Could not load the campaign" },
  });

  const {
    data: configs,
    isSuccess: configsLoaded,
    isError: configsError,
  } = useCustomFieldConfigs("Campaign");

  const {
    data: groups,
    isSuccess: groupsLoaded,
    isError: groupsError,
  } = useCustomFieldGroups("Campaign");

  if (campaignError || configsError || groupsError) {
    return (
      <span className="tw:text-destructive">Could not load the campaign.</span>
    );
  }

  if ((isEdit && !campaignLoaded) || !configsLoaded || !groupsLoaded) {
    return <FormSkeleton sections={4} className="tw:max-w-7xl" />;
  }

  return (
    <CampaignFormInner
      campaign={isEdit ? campaign : EMPTY_CAMPAIGN}
      configs={configs}
      groups={groups}
      onCancel={onCancel}
      onSuccess={onSuccess}
    />
  );
};

export default CampaignForm;
